import { authUrl } from '../api';

/**
 * Eingaben ueber eine Neuanmeldung retten (AC-016, AK-6).
 *
 * WARUM DAS UEBERHAUPT NOETIG IST. Die Anmeldung ist kein Dialog im Fenster,
 * sondern ein vollstaendiger Seitenwechsel: `SessionPanel` verlinkt auf
 * `/api/auth/login`, von dort geht es zu Microsoft und ueber
 * `/api/auth/callback` zurueck. Alles, was im Arbeitsspeicher der Seite stand,
 * ist danach weg — React-Zustand, halb ausgefuellte Formulare, die Mutation,
 * die gerade an der abgelaufenen Sitzung gescheitert ist.
 *
 * Und das ist kein Randfall: Die Sitzung laeuft nach acht Stunden absolut und
 * nach einer Stunde Leerlauf ab (AC-006). Wer nach der Mittagspause
 * weiterarbeitet, trifft genau diesen Fall — mit einem ausgefuellten Formular
 * vor sich.
 *
 * WARUM `sessionStorage` und nicht `localStorage`. Der Entwurf gehoert zu
 * DIESEM Tab und zu dieser Arbeitssitzung. `localStorage` ueberlebt das
 * Schliessen des Browsers und wuerde Wochen spaeter eine Eingabe anbieten, an
 * die sich niemand erinnert — auf einem womoeglich geteilten Rechner.
 *
 * AN DIE PERSON GEBUNDEN, nicht nur an den Tab. `sessionStorage` ueberlebt eine
 * Abmeldung und eine NEUE Anmeldung im selben Tab. Ohne Bindung bekaeme die
 * naechste Person, die sich an diesem Platz anmeldet, die getippten Werte der
 * vorigen angeboten — und schickte sie unter IHREM Namen ab. Im append-only
 * Audit-Log (AC-009) stuende dann sie als Urheberin eines Werts, den sie nie
 * eingegeben hat, und das ist nicht mehr korrigierbar. Bei 77 Nutzern mit
 * geteilten Arbeitsplaetzen ist das kein Gedankenspiel.
 *
 * Gebunden wird an dieselbe Kennung, die auch das Audit-Log als Urheber
 * festhaelt (`sessions.subject`, bei OIDC die Entra-`oid`) — nicht an eine
 * zweite, die daneben auseinanderlaufen koennte.
 *
 * WAS HIER NICHT HINEINGEHOERT: nichts Geheimes. Alles in `sessionStorage`
 * ist fuer jedes Skript auf dieser Herkunft lesbar. Das Sitzungscookie ist
 * `HttpOnly` und bleibt dem JavaScript ohnehin verborgen — das soll so bleiben.
 * Hier liegen Fachdaten, die der Anwender selbst gerade eingetippt hat, und
 * sonst nichts.
 */

/** Namensraum im Speicher, damit nichts mit fremden Eintraegen kollidiert. */
const PRAEFIX = 'asas.entwurf.';

/**
 * Wie lange ein Entwurf angeboten wird.
 *
 * Eine Stunde. Der Zweck ist, eine Neuanmeldung zu ueberbruecken — das dauert
 * Sekunden, im schlechtesten Fall die Zeit, die jemand mitten im Vorgang
 * weggeht. Die Leerlauffrist der Sitzung betraegt ebenfalls eine Stunde
 * (AC-006); laenger anzubieten hiesse, einen Entwurf zu einem Zustand
 * anzubieten, den der Server inzwischen sicher anders sieht.
 *
 * Das ist eine begruendete Setzung und keine Messung. Wenn die Oberflaeche
 * spaeter zeigt, dass Anwender laenger brauchen, ist der Wert hier zu aendern
 * und nirgends sonst.
 */
export const MAX_ENTWURFSALTER_MS = 60 * 60 * 1000;

export interface Entwurf<T> {
  readonly eingabe: T;
  /** Wann der Entwurf weggelegt wurde, als ISO-Zeichenkette. */
  readonly gespeichertAm: string;
}

export interface EntwurfsspeicherOptionen {
  /**
   * Die Kontokennung der angemeldeten Person — `subject` aus `loadSession()`.
   *
   * PFLICHT und nicht optional: Ein optionaler Inhaber waere ein Inhaber, den
   * irgendwann jemand weglaesst, und das Ergebnis waere der Fall oben — fremde
   * Eingaben unter eigenem Namen.
   */
  readonly inhaber: string;
  /** Einspeisbar fuer Tests; sonst `sessionStorage`. */
  readonly storage?: Storage;
}

/**
 * `this: void` an jeder Methode, und das ist keine Formalie: es sagt im TYP
 * zu, dass keine davon `this` benutzt. Damit ist `const { holen } = speicher`
 * nachweislich unbedenklich — und `@typescript-eslint/unbound-method`, das
 * genau diese Fragilitaet sucht, hat nichts mehr zu beanstanden. Waere die
 * Zusage nur ein Kommentar, haelt sie bis zur ersten Aenderung.
 */
export interface Entwurfsspeicher {
  /** Legt die Eingabe weg. `false`, wenn der Speicher nicht verfuegbar war. */
  merken<T>(this: void, schluessel: string, eingabe: T): boolean;
  /**
   * Holt die Eingabe zurueck — nur, wenn `pruefe` sie anerkennt.
   *
   * Der Pruefer ist PFLICHT und kein Komfort: Was hier herauskommt, ist Text
   * aus dem Browserspeicher, den jedes Skript auf dieser Herkunft und der
   * Anwender selbst veraendert haben kann. Ihn ungeprueft in eine Mutation zu
   * geben hiesse, beliebige Daten an den Server zu schicken und sie als
   * „Eingabe des Anwenders" auszugeben.
   */
  holen<T>(this: void, schluessel: string, pruefe: (roh: unknown) => T | null): Entwurf<T> | null;
  /** Wirft den Entwurf weg. Gehoert nach dem erfolgreichen Speichern aufgerufen. */
  vergessen(this: void, schluessel: string): void;
}

/**
 * Jeder Zugriff ist gekapselt, weil er WERFEN KANN.
 *
 * Im privaten Fenster, bei abgeschaltetem Speicher oder bei vollem Kontingent
 * wirft schon das blosse Lesen. Ein Entwurfsspeicher, der die Anwendung
 * abstuerzen laesst, waere schlimmer als gar keiner: der Anwender verloere die
 * Eingabe UND die Seite.
 */
function sicher<T>(wirkung: () => T, ersatz: T): T {
  try {
    return wirkung();
  } catch {
    return ersatz;
  }
}

/**
 * `storage` ist einspeisbar, damit der Test ihn stellen kann — und damit die
 * Anwendung ohne `sessionStorage` (serverseitiges Rendern der Tests) nicht
 * beim Laden des Moduls scheitert.
 */
export function erzeugeEntwurfsspeicher({
  inhaber,
  storage,
}: EntwurfsspeicherOptionen): Entwurfsspeicher {
  function speicher(): Storage | null {
    if (storage !== undefined) {
      return storage;
    }

    return sicher(() => (typeof sessionStorage === 'undefined' ? null : sessionStorage), null);
  }

  function vergessen(schluessel: string): void {
    const s = speicher();

    if (s === null) {
      return;
    }

    sicher(() => {
      s.removeItem(PRAEFIX + schluessel);
    }, undefined);
  }

  // KEIN `this` in den Methoden unten, und das ist Absicht: `holen` raeumt
  // selbst auf, und bei `const { holen } = speicher` waere `this` verloren —
  // der Aufruf schluege dann erst zur Laufzeit fehl, und nur im Fehlerpfad.
  return {
    vergessen,
    merken<T>(schluessel: string, eingabe: T): boolean {
      const s = speicher();

      if (s === null) {
        return false;
      }

      // ZUERST den alten Entwurf wegraeumen. Scheitert das Schreiben gleich
      // darauf (volles Kontingent, privates Fenster), bliebe er sonst stehen —
      // und `holen` faende nach der Neuanmeldung den AELTEREN Stand innerhalb
      // des Ein-Stunden-Fensters und boete ihn als aktuell an. Der Anwender
      // schickte dann stillschweigend veraltete Werte ab. Lieber nichts als
      // etwas Falsches: `merken` meldet den Fehlschlag mit `false`, und die
      // Oberflaeche kann ihn anzeigen. (Befund aus dem KI-Agent-Review.)
      vergessen(schluessel);

      return sicher(() => {
        const inhalt = {
          eingabe,
          gespeichertAm: new Date().toISOString(),
          inhaber,
        };

        // JSON und kein Transformer: Geld und Stunden liegen als ZEICHENKETTE
        // vor (README, „Drahtformat"), und genau deshalb ueberstehen sie diesen
        // Umweg unveraendert. Waeren sie `number`, verloere die Genauigkeit
        // hier ein zweites Mal — still, wie immer bei Dezimalwerten.
        s.setItem(PRAEFIX + schluessel, JSON.stringify(inhalt));

        return true;
      }, false);
    },

    holen<T>(schluessel: string, pruefe: (roh: unknown) => T | null): Entwurf<T> | null {
      const s = speicher();

      if (s === null) {
        return null;
      }

      const roh = sicher(() => s.getItem(PRAEFIX + schluessel), null);

      if (roh === null) {
        return null;
      }

      const geparst: unknown = sicher<unknown>(() => JSON.parse(roh), null);

      if (typeof geparst !== 'object' || geparst === null) {
        // Auch hier wegraeumen und nicht nur `null` liefern: ein unlesbarer
        // Eintrag bliebe sonst dauerhaft stehen und wuerde bei jedem Laden
        // erneut gelesen und erneut verworfen. Derselbe Grund wie unten beim
        // abgelehnten Entwurf — der erste Entwurf hatte ihn nur dort gezogen.
        vergessen(schluessel);
        return null;
      }

      const {
        eingabe,
        gespeichertAm,
        inhaber: gespeicherterInhaber,
      } = geparst as {
        eingabe?: unknown;
        gespeichertAm?: unknown;
        inhaber?: unknown;
      };

      if (gespeicherterInhaber !== inhaber) {
        // Fremder Entwurf — oder einer aus der Zeit vor dieser Pruefung. In
        // beiden Faellen WEG damit und nicht nur „nicht anbieten": Er gehoert
        // niemandem mehr, der an diesem Platz sitzt, und bliebe sonst bis zum
        // Schliessen des Tabs liegen. Dass die vorige Person ihn damit
        // verliert, ist der Preis — sie hat den Platz verlassen.
        vergessen(schluessel);
        return null;
      }

      if (typeof gespeichertAm !== 'string') {
        vergessen(schluessel);
        return null;
      }

      const alter = Date.now() - Date.parse(gespeichertAm);

      // `Number.isNaN` faengt den unlesbaren Zeitstempel mit ab; `alter < 0`
      // eine Uhr, die zurueckgestellt wurde.
      if (Number.isNaN(alter) || alter < 0 || alter > MAX_ENTWURFSALTER_MS) {
        vergessen(schluessel);
        return null;
      }

      // GEKAPSELT, obwohl `pruefe` vom Aufrufer kommt: die naheliegendste
      // Umsetzung ist ein Zod-Schema, und dessen `parse` WIRFT bei einer
      // Abweichung, statt `null` zu liefern. Ungekapselt risse ein veraenderter
      // Eintrag im Browserspeicher die ganze Ansicht beim Laden mit — genau
      // das, was `sicher(...)` an jeder anderen Stelle dieser Datei verhindert.
      const geprueft = sicher(() => pruefe(eingabe), null);

      // `=== null` GENUEGT NICHT, und das ist eine gemessene Falle: Die
      // natuerliche Zod-Schreibweise ohne Wurf ist `schema.safeParse(roh).data`,
      // und die liefert bei einer Abweichung `undefined`. Mit `=== null` kaeme
      // ein veraenderter Eintrag als `{ eingabe: undefined }` durch, getypt als
      // gueltiger Entwurf — und landete im Formular oder in der Mutation.
      // (Befund aus dem KI-Agent-Review.)
      if (geprueft === null || geprueft === undefined) {
        // Unbrauchbar. Weg damit, sonst wird er bei jedem Laden erneut
        // angeboten und jedes Mal erneut verworfen.
        vergessen(schluessel);
        return null;
      }

      return { eingabe: geprueft, gespeichertAm };
    },
  };
}

/**
 * Die Anmelde-URL, die AN DIESE STELLE zurueckfuehrt.
 *
 * Ohne `returnTo` landet der Anwender nach der Anmeldung auf der Startseite —
 * mit gerettetem Entwurf, aber auf dem falschen Bildschirm. „Ohne Verlust der
 * Eingabe" (AK-6) heisst beides: die Daten UND der Ort, an dem sie hingehoeren.
 *
 * Uebergeben wird ausschliesslich ein RELATIVER Pfad. Der Server prueft das
 * ohnehin noch einmal (`sanitizeReturnTo` in `oidc-transaction.ts` weist alles
 * ab, was nicht wie ein eigener Pfad aussieht, und faellt dann auf die
 * Startseite zurueck) — aber eine absolute URL hier hinzuschreiben hiesse, sich
 * auf diese Pruefung zu verlassen, statt gar nicht erst die Gelegenheit zu
 * einer offenen Weiterleitung zu schaffen.
 */
/**
 * Dieselbe Allowlist, die der Server anwendet — WOERTLICH.
 *
 * Quelle: `SAFE_RETURN_TO` in `apps/api/src/auth/oidc-transaction.ts`. Was ihr
 * nicht entspricht, verwirft der Server und faellt auf `/` zurueck. Beachte,
 * was NICHT erlaubt ist: `?`, `=`, `&`, `%` und der **Punkt**. Ein Pfad mit
 * Query-Anteil ueberlebt die Anmeldung also NICHT.
 *
 * Die Kopie hier ist Absicht und kein Versehen. Ohne sie schickt das Frontend
 * einen Wert, von dem es annimmt, er komme an — und der Anwender landet
 * stillschweigend auf der Startseite. Mit ihr weiss das Frontend vorher, was
 * durchgeht, und kann auf das Beste zurueckfallen, was noch durchkommt.
 *
 * WER DIE SERVERSEITIGE ALLOWLIST AENDERT, aendert diese hier mit. Damit das
 * keine blosse Bitte bleibt, LIEST `entwurf.spec.ts` die Quelldatei der API und
 * vergleicht die beiden Ausdruecke Zeichen fuer Zeichen — laufen sie
 * auseinander, wird er rot. Ein Test, der die Allowlist ein drittes Mal
 * abschriebe, waere genau die Pruefung, die nichts prueft.
 */
export const SICHERES_RETURN_TO = /^\/[A-Za-z0-9_\-/]*$/;

/** Wie `sanitizeReturnTo` am Server: Allowlist UND das ausdrueckliche `//`-Verbot. */
function kommtDurch(wert: string): boolean {
  // `//fremd` bestuende die Allowlist (alles erlaubte Zeichen), waere im
  // Browser aber eine protokollrelative ADRESSE und damit eine offene
  // Weiterleitung. Der Server prueft das gesondert, also hier ebenso.
  return (
    wert.length > 0 && wert.length <= 512 && SICHERES_RETURN_TO.test(wert) && !wert.startsWith('//')
  );
}

/**
 * Die Anmelde-URL, die moeglichst nah an DIESE Stelle zurueckfuehrt.
 *
 * Ohne `returnTo` landet der Anwender nach der Anmeldung auf der Startseite —
 * mit gerettetem Entwurf, aber auf dem falschen Bildschirm. „Ohne Verlust der
 * Eingabe" (AK-6) heisst beides: die Daten UND der Ort, an dem sie hingehoeren.
 *
 * DREI STUFEN, weil die Allowlist des Servers eng ist:
 *
 *   1. der Pfad, wie er hereingegeben wurde — wenn er durchkommt;
 *   2. sonst nur sein Pfadanteil ohne `?…` — ein Bildschirm ohne seinen Filter
 *      ist naeher dran als die Startseite;
 *   3. sonst `/`.
 *
 * Dass Stufe 2 noetig ist, ist eine echte Grenze und keine Vorsicht: Der Server
 * verbietet `?` in `returnTo`. Ein Aufrufer, der `location.pathname +
 * location.search` uebergibt, bekommt deshalb IMMER nur den Pfad zurueck. Wer
 * den Filterzustand ueber die Anmeldung retten will, legt ihn als Entwurf
 * daneben — dafuer ist der Speicher oben da.
 */
export function anmeldeUrlMitRueckkehr(pfad: string): string {
  // Auch am `#` trennen und nicht nur am `?`: Ein Fragment enthaelt dieselben
  // verbotenen Zeichen, und ohne diese Trennung uebersprunge ein Pfad mit
  // Fragment die zweite Stufe und landete auf `/` — genau das Ergebnis, das
  // die Stufen verhindern sollen. (Befund aus dem KI-Agent-Review.)
  const ohneAnhang = pfad.split(/[?#]/)[0] ?? '';
  const ziel = kommtDurch(pfad) ? pfad : kommtDurch(ohneAnhang) ? ohneAnhang : '/';

  return `${authUrl('login')}?returnTo=${encodeURIComponent(ziel)}`;
}
